import type { Listing } from '@/services/api';
import type { WishlistFilters } from '@/types/wishlist';
import { formatBrandName } from '@/utils/helpers/stringHelpers';
import { useMemo } from 'react';

export const useWishlistFiltering = (
    listings: Listing[],
    filters: WishlistFilters
): Listing[] => {
    return useMemo(() => {
        let filtered = [...listings];

        if (filters.brands.length > 0) {
            filtered = filtered.filter(listing => {
                const formattedListingBrand = formatBrandName(listing.product.brand);
                return filters.brands.includes(formattedListingBrand);
            });
        }

        if (filters.minPrice) {
            const minPrice = parseFloat(filters.minPrice);
            filtered = filtered.filter(listing => listing.current_price >= minPrice);
        }

        if (filters.maxPrice) {
            const maxPrice = parseFloat(filters.maxPrice);
            filtered = filtered.filter(listing => listing.current_price <= maxPrice);
        }

        if (filters.conditions.length > 0) {
            filtered = filtered.filter(listing =>
                filters.conditions.includes(listing.condition_name.toLowerCase())
            );
        }

        if (filters.vendors.length > 0) {
            filtered = filtered.filter(listing =>
                filters.vendors.includes(listing.vendor.toLowerCase())
            );
        }

        filtered.sort((a, b) => {
            const getDateTime = (listing: Listing) =>
                listing.datetime_scraped || new Date().toISOString();

            switch (filters.sortOrder) {
                case 'latest': {
                    return new Date(getDateTime(b)).getTime() - new Date(getDateTime(a)).getTime();
                }
                case 'price_lowest':
                    return a.current_price - b.current_price;
                case 'price_highest':
                    return b.current_price - a.current_price;
                case 'trending':
                case 'deal_best':
                default: {
                    const ratioA = a.market_status?.price_ratio || 0;
                    const ratioB = b.market_status?.price_ratio || 0;

                    if (ratioA !== ratioB) {
                        return ratioB - ratioA;
                    }

                    return new Date(getDateTime(b)).getTime() - new Date(getDateTime(a)).getTime();
                }
            }
        });

        return filtered;
    }, [listings, filters]);
};